import { useState, useEffect } from 'react';
import { Shield, Smartphone, Copy, Download, CheckCircle, X, ArrowRight, Key, Lock, Sparkles } from 'lucide-react';
import { use2FA, TwoFactorSetup } from '../hooks/use2FA';

interface TwoFactorSetupPageProps {
  onSetupComplete: () => void;
  onSkip: () => void;
}

export function TwoFactorSetupPage({ onSetupComplete, onSkip }: TwoFactorSetupPageProps) {
  const { initialize2FA, enable2FA } = use2FA();
  const [step, setStep] = useState<'intro' | 'scan' | 'verify' | 'complete'>('intro');
  const [setupData, setSetupData] = useState<TwoFactorSetup | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const animateStep = (newStep: typeof step) => {
    setIsVisible(false);
    setTimeout(() => {
      setStep(newStep);
      setIsVisible(true);
    }, 150);
  };

  const handleStartSetup = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await initialize2FA();
      setSetupData(data);
      animateStep('scan');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!setupData) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await enable2FA(verificationCode, setupData.secret, setupData.backupCodes);
      if (result.success) {
        animateStep('complete');
      } else {
        setError(result.error || 'Failed to enable 2FA');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    animateStep('intro');
    setSetupData(null);
    setVerificationCode('');
    setTimeout(() => {
      onSetupComplete();
    }, 300);
  };

  const copySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
    }
  };

  const downloadBackupCodes = () => {
    if (!setupData?.backupCodes) return;

    const content = `Payinthi DMS 2FA Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\n\nBackup Codes:\n${setupData.backupCodes.join('\n')}\n\nIMPORTANT:\n- Store these codes in a safe place\n- Each code can only be used once\n- Use these codes if you lose access to your authenticator app`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payinthi-dms-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      
      {/* Animated background particles */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-blue-400/20 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Floating orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl animate-pulse opacity-50" />
      <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-gradient-to-r from-indigo-500/10 to-pink-500/10 rounded-full blur-2xl animate-pulse opacity-50" style={{ animationDelay: '1s' }} />

      <div className={`bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-md border border-white/10 relative overflow-hidden transform transition-all duration-500 ${isVisible ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'}`}>
        
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-indigo-500/10" />
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-transparent rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-400/20 to-transparent rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }} />
        
        {/* Skip Button */}
        <button
          onClick={onSkip}
          className="absolute top-6 right-6 z-10 p-2 text-white/60 hover:text-white/90 transition-all duration-300 rounded-lg hover:bg-white/10 hover:scale-110 group"
        >
          <X className="w-5 h-5 transition-transform duration-300 group-hover:rotate-90" />
        </button>

        {/* Content */}
        <div className={`relative z-10 p-8 transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          
          {/* Intro Step */}
          {step === 'intro' && (
            <div className="text-center space-y-8">
              <div className="space-y-4">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto shadow-2xl transform transition-all duration-500 hover:scale-110 hover:rotate-3 group animate-pulse">
                  <Shield className="w-10 h-10 text-white transition-transform duration-300 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                </div>
                <h1 className="text-3xl font-bold text-white bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent animate-pulse">Enable 2FA</h1>
              </div>

              <div className="space-y-6">
                <button
                  onClick={handleStartSetup}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-blue-400 disabled:to-indigo-400 text-white px-6 py-4 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl font-medium text-lg flex items-center justify-center space-x-3 transform hover:scale-105 hover:-translate-y-1 group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin relative z-10" />
                  ) : (
                    <>
                      <Lock className="w-5 h-5 relative z-10 transition-transform duration-300 group-hover:scale-110" />
                      <span className="relative z-10">Get Started</span>
                      <ArrowRight className="w-5 h-5 relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>

                <button
                  onClick={onSkip}
                  className="w-full text-white/60 hover:text-white/90 px-6 py-3 rounded-2xl transition-all duration-300 font-medium hover:bg-white/5 transform hover:scale-105"
                >
                  Skip for now
                </button>
              </div>

              {error && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-4 text-red-300 text-sm transform animate-pulse">
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Scan Step */}
          {step === 'scan' && setupData && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto transform transition-all duration-500 hover:scale-110 hover:rotate-3 group animate-pulse">
                  <Smartphone className="w-8 h-8 text-white transition-transform duration-300 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                </div>
                <h2 className="text-2xl font-bold text-white bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">Scan QR Code</h2>
              </div>

              <div className="flex justify-center">
                <div className="bg-white p-6 rounded-3xl shadow-xl transform transition-all duration-500 hover:scale-105 hover:rotate-1 group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-100/20 to-emerald-100/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <img 
                    src={setupData.qrCodeUrl} 
                    alt="2FA QR Code" 
                    className="w-48 h-48 relative z-10 transition-transform duration-300 group-hover:scale-105"
                  />
                  {/* Scanning animation overlay */}
                  <div className="absolute inset-6 border-2 border-green-500/30 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute inset-0 border-2 border-green-400 rounded-2xl animate-ping" />
                  </div>
                </div>
              </div>

              <div className="bg-white/10 rounded-2xl p-4 space-y-3 transform transition-all duration-300 hover:bg-white/15 group">
                <div className="flex items-center space-x-2">
                  <Key className="w-4 h-4 text-white/70 transition-transform duration-300 group-hover:scale-110" />
                  <span className="text-white/70 text-sm font-medium">Manual Entry</span>
                </div>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 bg-black/20 px-3 py-2 rounded-lg text-sm font-mono text-white/90 border border-white/20 transition-all duration-300 group-hover:border-white/40">
                    {setupData.secret}
                  </code>
                  <button
                    onClick={copySecret}
                    className="p-2 text-white/60 hover:text-white/90 transition-all duration-300 hover:scale-110 hover:bg-white/10 rounded-lg group"
                  >
                    <Copy className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                  </button>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => animateStep('intro')}
                  className="flex-1 bg-white/10 text-white/90 px-6 py-3 rounded-xl transition-all duration-300 hover:bg-white/20 font-medium transform hover:scale-105 hover:-translate-y-1"
                >
                  Back
                </button>
                <button
                  onClick={() => animateStep('verify')}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl font-medium transform hover:scale-105 hover:-translate-y-1 group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="relative z-10">Continue</span>
                </button>
              </div>
            </div>
          )}

          {/* Verify Step */}
          {step === 'verify' && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto transform transition-all duration-500 hover:scale-110 hover:rotate-3 group animate-pulse">
                  <Key className="w-8 h-8 text-white transition-transform duration-300 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                </div>
                <h2 className="text-2xl font-bold text-white bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Enter Code</h2>
              </div>

              <div className="space-y-4">
                <div className="relative group">
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="w-full text-2xl text-center font-mono bg-white/10 border border-white/20 rounded-2xl px-4 py-4 focus:ring-2 focus:ring-purple-500 focus:border-transparent tracking-widest text-white placeholder-white/40 backdrop-blur-sm transition-all duration-300 group-hover:border-white/40 focus:scale-105"
                    maxLength={6}
                  />
                  {/* Input glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none blur-xl" />
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-4 text-red-300 text-sm transform animate-pulse">
                    {error}
                  </div>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => animateStep('scan')}
                  className="flex-1 bg-white/10 text-white/90 px-6 py-3 rounded-xl transition-all duration-300 hover:bg-white/20 font-medium transform hover:scale-105 hover:-translate-y-1"
                >
                  Back
                </button>
                <button
                  onClick={handleVerifyCode}
                  disabled={verificationCode.length !== 6 || loading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-purple-400 disabled:to-pink-400 text-white px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl font-medium disabled:cursor-not-allowed transform hover:scale-105 hover:-translate-y-1 group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto relative z-10" />
                  ) : (
                    <span className="relative z-10">Verify</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Complete Step */}
          {step === 'complete' && setupData && (
            <div className="space-y-6">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto transform transition-all duration-500 hover:scale-110 hover:rotate-3 group relative overflow-hidden">
                  <CheckCircle className="w-10 h-10 text-white transition-transform duration-300 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                  {/* Success animation */}
                  <div className="absolute inset-0 bg-green-400/30 rounded-2xl animate-ping" />
                </div>
                <h2 className="text-2xl font-bold text-white bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">All Set!</h2>
              </div>

              <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 space-y-4 transform transition-all duration-300 hover:bg-amber-500/25 group">
                <h3 className="font-bold text-amber-300 text-lg flex items-center space-x-2">
                  <Sparkles className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
                  <span>Backup Codes</span>
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {setupData.backupCodes?.map((code, index) => (
                    <code 
                      key={index} 
                      className="bg-black/20 px-3 py-2 rounded-lg text-sm font-mono text-center border border-white/20 text-white/90 transform transition-all duration-300 hover:scale-105 hover:bg-black/30 hover:border-amber-400/50"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      {code}
                    </code>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={downloadBackupCodes}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl transition-all duration-300 font-medium flex items-center justify-center space-x-2 transform hover:scale-105 hover:-translate-y-1 group"
                >
                  <Download className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                  <span>Save Codes</span>
                </button>
                <button
                  onClick={handleComplete}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl font-medium transform hover:scale-105 hover:-translate-y-1 group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="relative z-10">Continue</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}